import getAssets, {
  type AssetListItem,
  type GetAssetsParams,
} from '@cartwhl/common/api/getAssets';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type { PaginatedAsyncState } from '@cartwhl/common/types';

import * as globalStores from '../../stores/global';
import { blurFocusedElement } from '../shared/batchMotionPreviewPolling';
import { AbortableRequest } from './assetLibraryAbortableLoader';
import type { AssetLibraryDeleteTarget } from './assetLibraryAssetLifecycle';
import {
  type AssetLibraryBookmarkRequest,
  assetMatchesBookmarkRequest,
  assetMatchesDeleteTarget,
} from './assetLibraryAssetLifecycle';
import {
  ASSET_LIBRARY_ASSETS_CACHE_KEYS,
  ASSET_LIBRARY_CACHE_LOAD_KEYS,
  type AssetLibraryAssetsCacheKey,
  INITIAL_ASSET_LIBRARY_ASSETS_LOAD,
  getAssetsCacheKey,
  getAssetsLoadForCache,
  getMainLibraryAssets,
} from './assetLibraryAssetsLoad';
import { type AssetLibraryEmbedConfig } from './assetLibraryEmbedConfig';
import {
  type AssetLibraryFilters,
  type AssetLibraryLastModifiedFilter,
  type AssetLibraryTypeFilter,
  INITIAL_ASSET_LIBRARY_FILTERS,
  buildGetAssetsParamsFromFilters,
} from './assetLibraryFilters';
import type { AssetLibraryViewMode } from './assetLibraryGridLayout';
import {
  type AssetLibraryBatchGroupAssetType,
  type AssetLibraryGroupDetailLoadState,
  type AssetLibraryGroupDetailSelection,
  type AssetLibraryGroupKind,
  INITIAL_GROUP_DETAIL_LOAD_STATE,
  toGroupDetailSelection,
} from './assetLibraryGroupDetail';
import { fetchGroupDetailPage } from './assetLibraryGroupDetailLoader';
import {
  type AssetLibraryJobPreviewState,
  INITIAL_JOB_PREVIEW_STATE,
} from './assetLibraryJobPreview';
import { fetchJobPreviewData } from './assetLibraryJobPreviewLoader';
import {
  type AssetLibraryMediaPreviewState,
  INITIAL_MEDIA_PREVIEW_STATE,
  toMediaPreviewFromAsset,
} from './assetLibraryMediaPreview';
import {
  INITIAL_MOTION_LIBRARY_LOAD_STATE,
  MOTION_LIBRARY_SEARCH_DEBOUNCE_MS,
  type MotionLibraryDisplayItem,
  type MotionLibraryLoadState,
  getMotionLibraryLoadMode,
} from './assetLibraryMotionLibrary';
import { fetchMotionLibraryPage } from './assetLibraryMotionLibraryLoader';
import {
  type AssetLibraryStockMotionPreviewState,
  INITIAL_STOCK_MOTION_PREVIEW_STATE,
  fetchStockMotionPreviewBvhURL,
} from './assetLibraryStockMotionPreview';
import {
  type AssetLibraryView,
  INITIAL_ASSET_LIBRARY_VIEW,
} from './assetLibraryView';
import type { AssetLibraryDisplayItem } from './utils';
import { filterAssetsForDisplay, getAssetLibraryItemKey } from './utils';

const ASSETS_PAGE_SIZE = 20;

const INITIAL_LIBRARY_VIEW = {
  sortAscending: false,
  viewMode: 'grid' as AssetLibraryViewMode,
};

type Deps = { global: typeof globalStores };

type State = {
  mainAssetsLoad: PaginatedAsyncState<AssetListItem[]>;
  trashAssetsLoad: PaginatedAsyncState<AssetListItem[]>;
  bookmarksAssetsLoad: PaginatedAsyncState<AssetListItem[]>;
  filters: AssetLibraryFilters;
  libraryView: AssetLibraryView;
  sortAscending: boolean;
  viewMode: AssetLibraryViewMode;
  isLoadingMore: boolean;
  motionLibraryLoad: MotionLibraryLoadState;
  selectedGroup: AssetLibraryGroupDetailSelection | null;
  groupDetailLoad: AssetLibraryGroupDetailLoadState;
  jobPreview: AssetLibraryJobPreviewState;
  stockMotionPreview: AssetLibraryStockMotionPreviewState;
  mediaPreview: AssetLibraryMediaPreviewState;
  selectedAssetKeys: string[];
  selectedAssetCount: number;
};

function createInitialAssetLibraryState(): State {
  return {
    mainAssetsLoad: { ...INITIAL_ASSET_LIBRARY_ASSETS_LOAD },
    trashAssetsLoad: { ...INITIAL_ASSET_LIBRARY_ASSETS_LOAD },
    bookmarksAssetsLoad: { ...INITIAL_ASSET_LIBRARY_ASSETS_LOAD },
    filters: { ...INITIAL_ASSET_LIBRARY_FILTERS },
    libraryView: INITIAL_ASSET_LIBRARY_VIEW,
    ...INITIAL_LIBRARY_VIEW,
    isLoadingMore: false,
    motionLibraryLoad: INITIAL_MOTION_LIBRARY_LOAD_STATE,
    selectedGroup: null,
    groupDetailLoad: INITIAL_GROUP_DETAIL_LOAD_STATE,
    jobPreview: INITIAL_JOB_PREVIEW_STATE,
    stockMotionPreview: INITIAL_STOCK_MOTION_PREVIEW_STATE,
    mediaPreview: INITIAL_MEDIA_PREVIEW_STATE,
    selectedAssetKeys: [],
    selectedAssetCount: 0,
  };
}

class AssetLibraryStore extends BaseStore<State, Deps> {
  private listParamsSnapshotByCache: Partial<
    Record<AssetLibraryAssetsCacheKey, string>
  > = {};
  private embedConfig: AssetLibraryEmbedConfig | null = null;
  private motionLibraryRequest = new AbortableRequest();
  private motionLibrarySearchTimeout: ReturnType<typeof setTimeout> | null =
    null;
  private groupDetailRequest = new AbortableRequest();
  private jobPreviewRequest = new AbortableRequest();
  private stockMotionPreviewRequest = new AbortableRequest();

  private fetchAssetsPage = async (
    apiKey: string,
    params: GetAssetsParams,
    nextToken?: string,
  ) => {
    const { data, error } = await getAssets(apiKey, {
      ...params,
      limit: ASSETS_PAGE_SIZE,
      nextToken,
      sortAscending: params.sortAscending ?? false,
    });

    if (error) {
      return { error: { message: error.message } };
    }

    return {
      data: data?.assets ?? [],
      nextToken: data?.nextToken,
    };
  };

  readonly mainAssetsLoader = this.createPaginatedLoader(
    'mainAssetsLoad',
    this.fetchAssetsPage,
  );

  readonly trashAssetsLoader = this.createPaginatedLoader(
    'trashAssetsLoad',
    this.fetchAssetsPage,
  );

  readonly bookmarksAssetsLoader = this.createPaginatedLoader(
    'bookmarksAssetsLoad',
    this.fetchAssetsPage,
  );

  constructor(deps: Deps) {
    super(createInitialAssetLibraryState(), deps);
  }

  init() {
    void this.loadAssets();

    this.subscribeTo(
      this.deps.global.authStore.state,
      () => {
        void this.loadAssets();
      },
      { keys: 'authStatus' },
    );

    this.subscribeTo(
      this.deps.global.userStore.state,
      () => {
        this.invalidateAllAssetsCaches();
        void this.loadAssets(undefined, { force: true });
      },
      { keys: ['apiKey', 'loading', 'userInfo'] },
    );
  }

  reset() {
    this.listParamsSnapshotByCache = {};
    this.embedConfig = null;
    this.state.filters = { ...INITIAL_ASSET_LIBRARY_FILTERS };
    this.state.libraryView = INITIAL_ASSET_LIBRARY_VIEW;
    Object.assign(this.state, INITIAL_LIBRARY_VIEW);
    this.state.isLoadingMore = false;
    this.resetMotionLibrary();
    this.closeGroupDetail();
    this.closeJobPreview();
    this.closeStockMotionPreview();
    this.closeMediaPreview();
    this.clearAssetSelection();
    this.mainAssetsLoader.reset();
    this.trashAssetsLoader.reset();
    this.bookmarksAssetsLoader.reset();
  }

  destroy() {
    this.clearMotionLibrarySearchTimeout();
    this.abortMotionLibraryRequest();
    this.abortGroupDetailRequest();
    this.abortJobPreviewRequest();
    this.abortStockMotionPreviewRequest();
    super.destroy();
  }

  setEmbedConfig(embedConfig: AssetLibraryEmbedConfig | null) {
    this.embedConfig = embedConfig;
  }

  getEmbedConfig() {
    return this.embedConfig;
  }

  initializeForEmbed() {
    void this.loadAssets(this.buildListParams(), { force: true });
  }

  openJobPreview(input: {
    jobId: string;
    groupID: string;
    assetType: AssetLibraryBatchGroupAssetType;
    groupKind: AssetLibraryGroupKind;
    isBookmarked?: boolean;
    showMainLibraryPreviewActions?: boolean;
  }) {
    blurFocusedElement();
    this.closeStockMotionPreview();
    this.abortJobPreviewRequest();
    this.state.jobPreview = {
      ...INITIAL_JOB_PREVIEW_STATE,
      status: 'pending',
      jobId: input.jobId,
      groupID: input.groupID,
      groupKind: input.groupKind,
      assetType: input.assetType,
      isBookmarked: Boolean(input.isBookmarked),
      showMainLibraryPreviewActions: Boolean(
        input.showMainLibraryPreviewActions,
      ),
    };
    void this.loadJobPreviewData();
  }

  setPreviewJobId(jobId: string) {
    if (this.state.jobPreview.status !== 'ready') {
      return;
    }

    this.state.jobPreview.jobId = jobId;
  }

  closeJobPreview() {
    this.abortJobPreviewRequest();
    this.state.jobPreview = INITIAL_JOB_PREVIEW_STATE;
  }

  openStockMotionPreview(motion: MotionLibraryDisplayItem) {
    blurFocusedElement();
    this.closeJobPreview();
    this.closeMediaPreview();
    this.abortStockMotionPreviewRequest();
    this.state.stockMotionPreview = {
      ...INITIAL_STOCK_MOTION_PREVIEW_STATE,
      status: 'pending',
      motionId: motion.motionID,
      title: motion.title,
    };
    void this.loadStockMotionPreviewData();
  }

  closeStockMotionPreview() {
    this.abortStockMotionPreviewRequest();
    this.state.stockMotionPreview = INITIAL_STOCK_MOTION_PREVIEW_STATE;
  }

  openMediaPreview(
    input: Pick<
      AssetLibraryMediaPreviewState,
      | 'title'
      | 'mediaUrl'
      | 'mediaType'
      | 'createdAt'
      | 'createdBy'
      | 'isBookmarked'
      | 'showMainLibraryPreviewActions'
      | 'mediaSelection'
      | 'showOpenWithActions'
    >,
  ) {
    blurFocusedElement();
    this.state.mediaPreview = {
      open: true,
      ...input,
    };
  }

  openMediaPreviewFromAsset(
    asset: AssetLibraryDisplayItem,
    options?: {
      showMainLibraryPreviewActions?: boolean;
      showOpenWithActions?: boolean;
    },
  ) {
    const preview = toMediaPreviewFromAsset(asset);
    if (!preview) {
      return;
    }

    this.openMediaPreview({
      ...preview,
      showMainLibraryPreviewActions: Boolean(
        options?.showMainLibraryPreviewActions,
      ),
      showOpenWithActions: Boolean(options?.showOpenWithActions),
    });
  }

  closeMediaPreview() {
    this.state.mediaPreview = INITIAL_MEDIA_PREVIEW_STATE;
  }

  openGroupDetail(asset: AssetLibraryDisplayItem) {
    const selection = toGroupDetailSelection(asset);
    if (!selection) {
      return;
    }

    this.clearAssetSelection();
    this.state.selectedGroup = selection;
    this.abortGroupDetailRequest();
    this.state.groupDetailLoad = {
      ...INITIAL_GROUP_DETAIL_LOAD_STATE,
      status: 'pending',
    };
    void this.fetchGroupDetail({ reset: true });
  }

  closeGroupDetail() {
    this.abortGroupDetailRequest();
    this.state.selectedGroup = null;
    this.state.groupDetailLoad = INITIAL_GROUP_DETAIL_LOAD_STATE;
  }

  async loadMoreGroupDetail() {
    const { groupDetailLoad, selectedGroup } = this.state;
    if (!selectedGroup || groupDetailLoad.isLoadingMore) {
      return;
    }

    if (groupDetailLoad.status === 'ready' && !groupDetailLoad.hasMore) {
      return;
    }

    const isInitialFetch = groupDetailLoad.status === 'initial';
    await this.fetchGroupDetail({ reset: isInitialFetch });
  }

  setSearchQuery(searchQuery: string) {
    this.state.filters.searchQuery = searchQuery;
    this.resetMotionLibrary();
    this.scheduleMotionLibrarySync();
  }

  setTypeFilter(typeFilter: AssetLibraryTypeFilter) {
    this.state.filters.typeFilter = typeFilter;
    this.resetMotionLibrary();
    void this.reloadWithCurrentFilters();
  }

  setLastModifiedFilter(lastModifiedFilter: AssetLibraryLastModifiedFilter) {
    this.state.filters.lastModifiedFilter = lastModifiedFilter;
    this.resetMotionLibrary();
    void this.reloadWithCurrentFilters();
  }

  setCreatedByUserFilter(createdByUser: string) {
    this.state.filters.createdByUser = createdByUser;
    this.resetMotionLibrary();
    void this.reloadWithCurrentFilters();
  }

  toggleSortAscending() {
    this.state.sortAscending = !this.state.sortAscending;
    this.resetMotionLibrary();
    void this.reloadWithCurrentFilters();
  }

  toggleViewMode() {
    this.state.viewMode = this.state.viewMode === 'grid' ? 'list' : 'grid';
  }

  setLibraryView(libraryView: AssetLibraryView) {
    if (this.state.libraryView === libraryView) {
      return;
    }

    this.clearAssetSelection();
    this.closeGroupDetail();
    this.state.libraryView = libraryView;
    this.resetMotionLibrary();

    const cacheKey = getAssetsCacheKey(libraryView);
    if (this.hasValidAssetsCache(cacheKey)) {
      return;
    }

    void this.reloadWithCurrentFilters();
  }

  toggleAssetSelection(asset: AssetLibraryDisplayItem) {
    const key = getAssetLibraryItemKey(asset);
    const selectedAssetKeys = this.state.selectedAssetKeys;

    if (selectedAssetKeys.includes(key)) {
      this.state.selectedAssetKeys = selectedAssetKeys.filter(
        (selectedKey) => selectedKey !== key,
      );
    } else {
      this.state.selectedAssetKeys = [...selectedAssetKeys, key];
    }

    this.state.selectedAssetCount = this.state.selectedAssetKeys.length;
  }

  pruneAssetSelectionKeys(selectableKeys: readonly string[]) {
    const selectableKeySet = new Set(selectableKeys);
    const prunedKeys = this.state.selectedAssetKeys.filter((key) =>
      selectableKeySet.has(key),
    );

    if (prunedKeys.length === this.state.selectedAssetKeys.length) {
      return;
    }

    this.state.selectedAssetKeys = prunedKeys;
    this.state.selectedAssetCount = prunedKeys.length;
  }

  isAssetSelectionKeySelected(key: string) {
    return this.state.selectedAssetKeys.includes(key);
  }

  clearAssetSelection() {
    this.state.selectedAssetKeys = [];
    this.state.selectedAssetCount = 0;
  }

  getSelectedDisplayAssets(): AssetLibraryDisplayItem[] {
    const assets = this.getActiveAssetsLoad().data?.items ?? [];
    const displayAssets = filterAssetsForDisplay(
      assets,
      this.state.filters,
      this.embedConfig,
      this.state.sortAscending,
      this.state.libraryView,
    );
    const selectedKeys = new Set(this.state.selectedAssetKeys);

    return displayAssets.filter((asset) =>
      selectedKeys.has(getAssetLibraryItemKey(asset)),
    );
  }

  applyAssetLifecyclePatch(
    matcher: (asset: AssetListItem) => boolean,
    patch: Partial<Pick<AssetListItem, 'isBookmarked' | 'isDeleted'>>,
  ) {
    for (const cacheKey of ASSET_LIBRARY_ASSETS_CACHE_KEYS) {
      const items = this.getAssetsLoadForCache(cacheKey).data?.items;
      if (!items) {
        continue;
      }

      for (const asset of items) {
        if (!matcher(asset)) {
          continue;
        }

        if (patch.isBookmarked !== undefined) {
          asset.isBookmarked = patch.isBookmarked;
        }

        if (patch.isDeleted !== undefined) {
          asset.isDeleted = patch.isDeleted;
        }
      }
    }
  }

  applyBookmarkChange(
    request: AssetLibraryBookmarkRequest,
    bookmarked: boolean,
    sourceAsset?: AssetLibraryDisplayItem,
  ) {
    this.applyAssetLifecyclePatch(
      (asset) => assetMatchesBookmarkRequest(asset, request),
      { isBookmarked: bookmarked },
    );

    const activeKey = getAssetsCacheKey(this.state.libraryView);

    if (activeKey === 'bookmarks') {
      if (bookmarked) {
        this.addBookmarkedAssetToCache(request, sourceAsset);
      } else {
        this.removeAssetsMatchingBookmarkRequestFromCache('bookmarks', request);
      }
    }

    this.invalidateInactiveAssetCaches(activeKey);
  }

  markAssetsDeleted(targets: AssetLibraryDeleteTarget[]) {
    this.applyAssetLifecyclePatch(
      (asset) =>
        targets.some((target) => assetMatchesDeleteTarget(asset, target)),
      { isDeleted: true },
    );

    const activeKey = getAssetsCacheKey(this.state.libraryView);

    if (activeKey === 'main') {
      this.removeAssetsMatchingDeleteTargetsFromCache('main', targets);
    } else if (activeKey === 'bookmarks') {
      this.removeAssetsMatchingDeleteTargetsFromCache('bookmarks', targets);
    }

    this.invalidateInactiveAssetCaches(activeKey);
  }

  markAssetsRestored(targets: AssetLibraryDeleteTarget[]) {
    this.applyAssetLifecyclePatch(
      (asset) =>
        targets.some((target) => assetMatchesDeleteTarget(asset, target)),
      { isDeleted: false },
    );

    const activeKey = getAssetsCacheKey(this.state.libraryView);

    if (activeKey === 'trash') {
      this.removeAssetsMatchingDeleteTargetsFromCache('trash', targets);
    }

    this.invalidateInactiveAssetCaches(activeKey);
  }

  setPreviewBookmarkState(isBookmarked: boolean) {
    if (this.state.mediaPreview.open) {
      this.state.mediaPreview.isBookmarked = isBookmarked;
    }

    if (this.state.jobPreview.jobId) {
      this.state.jobPreview.isBookmarked = isBookmarked;
    }
  }

  closePreviewsAfterTrash() {
    this.closeMediaPreview();
    this.closeJobPreview();
    this.closeStockMotionPreview();
  }

  async loadMoreContent() {
    const assetsLoad = this.getActiveAssetsLoad();
    const hasMoreAssets =
      assetsLoad.status === 'ready' && Boolean(assetsLoad.data?.nextToken);

    if (hasMoreAssets) {
      await this.loadMoreAssets();
      return;
    }

    await this.loadMoreMotionLibrary();
  }

  private async loadMoreAssets(): Promise<void> {
    if (this.state.isLoadingMore) {
      return;
    }

    const assetsLoad = this.getActiveAssetsLoad();
    if (assetsLoad.status !== 'ready' || !assetsLoad.data?.nextToken) {
      return;
    }

    this.state.isLoadingMore = true;
    try {
      await this.getActiveAssetsLoader().fetchMore();
      this.syncMotionLibraryAfterAssetsChange();
    } finally {
      this.state.isLoadingMore = false;
    }
  }

  getMotionLibraryMode() {
    const assets = this.getActiveAssetsLoad().data?.items ?? [];
    const displayAssets = filterAssetsForDisplay(
      assets,
      this.state.filters,
      this.embedConfig,
      this.state.sortAscending,
      this.state.libraryView,
    );

    return getMotionLibraryLoadMode({
      filters: this.state.filters,
      assetCount: assets.length,
      displayAssetCount: displayAssets.length,
      assetsStatus: this.getActiveAssetsLoad().status,
      assetsExhausted:
        this.getActiveAssetsLoad().status === 'ready' &&
        !this.getActiveAssetsLoad().data?.nextToken,
      disableMotionLibrary:
        (this.embedConfig?.disableMotionLibrary ?? false) ||
        this.state.libraryView !== 'all',
    });
  }

  private async reloadWithCurrentFilters() {
    const cacheKey = getAssetsCacheKey(this.state.libraryView);
    await this.loadAssets(this.buildListParamsForCache(cacheKey), {
      force: true,
      cacheKey,
    });
  }

  private buildListParamsForCache(
    cacheKey: AssetLibraryAssetsCacheKey,
  ): GetAssetsParams {
    const userID = this.deps.global.userStore.state.userInfo.userID;

    return {
      ...buildGetAssetsParamsFromFilters(this.state.filters, this.embedConfig),
      sortAscending: this.state.sortAscending,
      ...(userID ? { requestedByUser: userID } : {}),
      ...(cacheKey === 'trash' ? { isDeleted: true } : {}),
      ...(cacheKey === 'bookmarks' ? { isBookmarked: true } : {}),
    };
  }

  private buildListParams(): GetAssetsParams {
    return this.buildListParamsForCache(
      getAssetsCacheKey(this.state.libraryView),
    );
  }

  private invalidateInactiveAssetCaches(activeKey: AssetLibraryAssetsCacheKey) {
    for (const cacheKey of ASSET_LIBRARY_ASSETS_CACHE_KEYS) {
      if (cacheKey !== activeKey) {
        this.invalidateAssetsCache(cacheKey);
      }
    }
  }

  private invalidateAssetsCache(cacheKey: AssetLibraryAssetsCacheKey) {
    delete this.listParamsSnapshotByCache[cacheKey];
    this.getAssetsLoaderForCache(cacheKey).reset();
  }

  private invalidateAllAssetsCaches() {
    this.listParamsSnapshotByCache = {};
    this.mainAssetsLoader.reset();
    this.trashAssetsLoader.reset();
    this.bookmarksAssetsLoader.reset();
  }

  private getListParamsSnapshot(params: GetAssetsParams): string {
    return JSON.stringify(params);
  }

  private hasValidAssetsCache(cacheKey: AssetLibraryAssetsCacheKey): boolean {
    const assetsLoad = this.getAssetsLoadForCache(cacheKey);
    const cachedSnapshot = this.listParamsSnapshotByCache[cacheKey];
    const currentSnapshot = this.getListParamsSnapshot(
      this.buildListParamsForCache(cacheKey),
    );

    if (!cachedSnapshot || cachedSnapshot !== currentSnapshot) {
      return false;
    }

    if (assetsLoad.status === 'ready') {
      return true;
    }

    return (
      assetsLoad.status === 'pending' &&
      (assetsLoad.data?.items?.length ?? 0) > 0
    );
  }

  private getAssetsLoadForCache(
    cacheKey: AssetLibraryAssetsCacheKey,
  ): PaginatedAsyncState<AssetListItem[]> {
    return getAssetsLoadForCache(this.state, cacheKey);
  }

  private setAssetsLoadForCache(
    cacheKey: AssetLibraryAssetsCacheKey,
    value: PaginatedAsyncState<AssetListItem[]>,
  ) {
    this.state[ASSET_LIBRARY_CACHE_LOAD_KEYS[cacheKey]] = value;
  }

  private removeAssetsMatchingDeleteTargetsFromCache(
    cacheKey: AssetLibraryAssetsCacheKey,
    targets: AssetLibraryDeleteTarget[],
  ) {
    const loadState = this.getAssetsLoadForCache(cacheKey);
    const items = loadState.data?.items;
    if (!items) {
      return;
    }

    const nextItems = items.filter(
      (asset) =>
        !targets.some((target) => assetMatchesDeleteTarget(asset, target)),
    );

    if (nextItems.length === items.length) {
      return;
    }

    this.setAssetsLoadForCache(cacheKey, {
      status: 'ready',
      data: {
        items: nextItems,
        nextToken: loadState.data?.nextToken,
      },
    });
  }

  private removeAssetsMatchingBookmarkRequestFromCache(
    cacheKey: AssetLibraryAssetsCacheKey,
    request: AssetLibraryBookmarkRequest,
  ) {
    const loadState = this.getAssetsLoadForCache(cacheKey);
    const items = loadState.data?.items;
    if (!items) {
      return;
    }

    const nextItems = items.filter(
      (asset) => !assetMatchesBookmarkRequest(asset, request),
    );

    if (nextItems.length === items.length) {
      return;
    }

    this.setAssetsLoadForCache(cacheKey, {
      status: 'ready',
      data: {
        items: nextItems,
        nextToken: loadState.data?.nextToken,
      },
    });
  }

  private addBookmarkedAssetToCache(
    request: AssetLibraryBookmarkRequest,
    sourceAsset?: AssetLibraryDisplayItem,
  ) {
    const loadState = this.getAssetsLoadForCache('bookmarks');
    const existingItems = loadState.data?.items;
    if (!existingItems) {
      return;
    }

    const assetToAdd =
      sourceAsset ??
      getMainLibraryAssets(this.state).find((item) =>
        assetMatchesBookmarkRequest(item, request),
      );

    if (!assetToAdd) {
      return;
    }

    if (
      existingItems.some((item) => assetMatchesBookmarkRequest(item, request))
    ) {
      return;
    }

    this.setAssetsLoadForCache('bookmarks', {
      status: 'ready',
      data: {
        items: [
          { ...assetToAdd, isBookmarked: true, isDeleted: false },
          ...existingItems,
        ],
        nextToken: loadState.data?.nextToken,
      },
    });
  }

  private getActiveAssetsLoad(): PaginatedAsyncState<AssetListItem[]> {
    return this.getAssetsLoadForCache(
      getAssetsCacheKey(this.state.libraryView),
    );
  }

  private getAssetsLoaderForCache(cacheKey: AssetLibraryAssetsCacheKey) {
    switch (cacheKey) {
      case 'trash':
        return this.trashAssetsLoader;
      case 'bookmarks':
        return this.bookmarksAssetsLoader;
      default:
        return this.mainAssetsLoader;
    }
  }

  private getActiveAssetsLoader() {
    return this.getAssetsLoaderForCache(
      getAssetsCacheKey(this.state.libraryView),
    );
  }

  private async loadAssets(
    params?: GetAssetsParams,
    options: { force?: boolean; cacheKey?: AssetLibraryAssetsCacheKey } = {},
  ) {
    const { authStore, userStore } = this.deps.global;

    if (authStore.state.authStatus !== 'user') {
      this.reset();
      return;
    }

    const { apiKey, loading } = userStore.state;
    const apiKeyValue = apiKey.data?.apiKey;
    const cacheKey =
      options.cacheKey ?? getAssetsCacheKey(this.state.libraryView);
    const loader = this.getAssetsLoaderForCache(cacheKey);
    const listParams = params ?? this.buildListParamsForCache(cacheKey);

    if (apiKeyValue) {
      this.listParamsSnapshotByCache[cacheKey] =
        this.getListParamsSnapshot(listParams);

      if (options.force) {
        this.state.isLoadingMore = false;
        this.resetMotionLibrary();

        const staleItems = this.getAssetsLoadForCache(cacheKey).data?.items;
        if (staleItems && staleItems.length > 0) {
          this.setAssetsLoadForCache(cacheKey, {
            status: 'pending',
            data: {
              items: staleItems,
              nextToken: undefined,
            },
          });
        } else {
          loader.reset();
        }
      }

      await loader.fetch(apiKeyValue, listParams);
      this.syncMotionLibraryAfterAssetsChange();
      return;
    }

    if (
      loading ||
      ((apiKey.status === 'initial' || apiKey.status === 'pending') &&
        !apiKey.data)
    ) {
      this.setAssetsLoadForCache(cacheKey, {
        status: 'pending',
        data: this.getAssetsLoadForCache(cacheKey).data,
      });
      return;
    }

    if (apiKey.status === 'error') {
      this.setAssetsLoadForCache(cacheKey, {
        status: 'error',
        error: apiKey.error ?? 'Unable to load API key.',
        data: undefined,
      });
      return;
    }

    this.setAssetsLoadForCache(cacheKey, {
      status: 'ready',
      data: { items: [] },
    });
    this.syncMotionLibraryAfterAssetsChange();
  }

  private getApiKeyValue(): string | undefined {
    return this.deps.global.userStore.state.apiKey.data?.apiKey;
  }

  private resetMotionLibrary() {
    this.clearMotionLibrarySearchTimeout();
    this.abortMotionLibraryRequest();
    this.state.motionLibraryLoad = INITIAL_MOTION_LIBRARY_LOAD_STATE;
  }

  private clearMotionLibrarySearchTimeout() {
    if (this.motionLibrarySearchTimeout) {
      clearTimeout(this.motionLibrarySearchTimeout);
      this.motionLibrarySearchTimeout = null;
    }
  }

  private abortMotionLibraryRequest() {
    this.motionLibraryRequest.abort();
  }

  private scheduleMotionLibrarySync() {
    this.clearMotionLibrarySearchTimeout();

    const mode = this.getMotionLibraryMode();
    if (mode === 'none') {
      return;
    }

    this.motionLibrarySearchTimeout = setTimeout(() => {
      this.motionLibrarySearchTimeout = null;
      void this.fetchMotionLibrary({ reset: true });
    }, MOTION_LIBRARY_SEARCH_DEBOUNCE_MS);
  }

  private syncMotionLibraryAfterAssetsChange() {
    const mode = this.getMotionLibraryMode();

    if (mode === 'none') {
      this.resetMotionLibrary();
      return;
    }

    // Asset pagination can call this after every page. If motion library is
    // already loading or loaded for the current query, leave it alone so we
    // don't wipe page 1 and flicker results while auto-filling the viewport.
    const { status } = this.state.motionLibraryLoad;
    if (status === 'pending' || status === 'ready') {
      return;
    }

    if (mode === 'fallback') {
      void this.fetchMotionLibrary({ reset: true });
      return;
    }

    this.scheduleMotionLibrarySync();
  }

  private async loadMoreMotionLibrary() {
    const mode = this.getMotionLibraryMode();
    if (mode === 'none') {
      return;
    }

    const { motionLibraryLoad } = this.state;
    if (motionLibraryLoad.isLoadingMore) {
      return;
    }

    const isInitialFetch = motionLibraryLoad.status === 'initial';
    if (
      !isInitialFetch &&
      motionLibraryLoad.status === 'ready' &&
      !motionLibraryLoad.hasMore
    ) {
      return;
    }

    await this.fetchMotionLibrary({ reset: isInitialFetch });
  }

  private async fetchMotionLibrary({ reset }: { reset: boolean }) {
    const apiKey = this.getApiKeyValue();
    const mode = this.getMotionLibraryMode();

    if (!apiKey || mode === 'none') {
      return;
    }

    const { motionLibraryLoad } = this.state;
    if (
      !reset &&
      motionLibraryLoad.status === 'ready' &&
      !motionLibraryLoad.hasMore
    ) {
      return;
    }

    if (reset) {
      this.abortMotionLibraryRequest();
      this.state.motionLibraryLoad = {
        ...INITIAL_MOTION_LIBRARY_LOAD_STATE,
        status: 'pending',
      };
    } else {
      this.state.motionLibraryLoad.isLoadingMore = true;
    }

    const signal = this.motionLibraryRequest.start();

    try {
      const searchTerm = this.state.filters.searchQuery.trim();
      const page = await fetchMotionLibraryPage({
        apiKey,
        searchTerm,
        lastSort: reset ? undefined : motionLibraryLoad.lastSort,
        signal,
      });

      if (signal.aborted) {
        return;
      }

      const previousItems = reset ? [] : this.state.motionLibraryLoad.items;

      this.state.motionLibraryLoad = {
        status: 'ready',
        items: [...previousItems, ...page.items],
        hasMore: page.hasMore,
        lastSort: page.lastSort,
        isLoadingMore: false,
      };
    } catch (error) {
      if (signal.aborted) {
        return;
      }

      this.state.motionLibraryLoad = {
        ...this.state.motionLibraryLoad,
        status: 'error',
        error:
          error instanceof Error
            ? error.message
            : 'Unable to load motion library.',
        isLoadingMore: false,
      };
    } finally {
      if (!signal.aborted) {
        this.motionLibraryRequest.complete();
      }
    }
  }

  private abortGroupDetailRequest() {
    this.groupDetailRequest.abort();
  }

  private async fetchGroupDetail({ reset }: { reset: boolean }) {
    const apiKey = this.getApiKeyValue();
    const { selectedGroup, groupDetailLoad } = this.state;

    if (!apiKey || !selectedGroup) {
      return;
    }

    if (
      !reset &&
      groupDetailLoad.status === 'ready' &&
      !groupDetailLoad.hasMore
    ) {
      return;
    }

    if (reset) {
      this.abortGroupDetailRequest();
      this.state.groupDetailLoad = {
        ...INITIAL_GROUP_DETAIL_LOAD_STATE,
        status: 'pending',
      };
    } else {
      this.state.groupDetailLoad.isLoadingMore = true;
    }

    const signal = this.groupDetailRequest.start();

    try {
      const nextToken = reset ? undefined : groupDetailLoad.nextToken;
      const page = await fetchGroupDetailPage(selectedGroup, apiKey, nextToken);

      if (signal.aborted) {
        return;
      }

      const previousItems = reset ? [] : this.state.groupDetailLoad.items;
      const previousMotions = reset
        ? []
        : this.state.groupDetailLoad.projectMotions;

      this.state.groupDetailLoad = {
        status: 'ready',
        items: [...previousItems, ...page.items],
        projectMotions: [...previousMotions, ...page.projectMotions],
        nextToken: page.nextToken,
        hasMore: Boolean(page.nextToken),
        isLoadingMore: false,
      };
    } catch (error) {
      if (signal.aborted) {
        return;
      }

      this.state.groupDetailLoad = {
        ...this.state.groupDetailLoad,
        status: 'error',
        error:
          error instanceof Error
            ? error.message
            : 'Unable to load batch items.',
        isLoadingMore: false,
      };
    } finally {
      if (!signal.aborted) {
        this.groupDetailRequest.complete();
      }
    }
  }

  private abortJobPreviewRequest() {
    this.jobPreviewRequest.abort();
  }

  private abortStockMotionPreviewRequest() {
    this.stockMotionPreviewRequest.abort();
  }

  private getProjectMotionsCache() {
    const { selectedGroup, groupDetailLoad, jobPreview } = this.state;

    if (
      jobPreview.groupKind !== 'project' ||
      !jobPreview.groupID ||
      !selectedGroup ||
      selectedGroup.groupID !== jobPreview.groupID
    ) {
      return null;
    }

    if (
      groupDetailLoad.status === 'initial' ||
      groupDetailLoad.status === 'error' ||
      (groupDetailLoad.status === 'pending' &&
        groupDetailLoad.projectMotions.length === 0)
    ) {
      return null;
    }

    return {
      groupID: jobPreview.groupID,
      motions: groupDetailLoad.projectMotions,
      hasMore: groupDetailLoad.hasMore,
      nextToken: groupDetailLoad.nextToken,
    };
  }

  private async loadJobPreviewData() {
    const apiKey = this.getApiKeyValue();
    const { jobPreview } = this.state;

    if (!apiKey || !jobPreview.groupID || !jobPreview.assetType) {
      this.state.jobPreview = {
        ...INITIAL_JOB_PREVIEW_STATE,
        status: 'error',
        error: 'Unable to load preview.',
      };
      return;
    }

    const signal = this.jobPreviewRequest.start();

    try {
      const result = await fetchJobPreviewData({
        groupID: jobPreview.groupID,
        groupKind: jobPreview.groupKind!,
        assetType: jobPreview.assetType,
        apiKey,
        projectMotionsCache: this.getProjectMotionsCache(),
      });

      if (signal.aborted) {
        return;
      }

      this.state.jobPreview = {
        ...this.state.jobPreview,
        status: 'ready',
        motions: result.motions,
        mascots: result.mascots,
        batchExportSettings: result.batchExportSettings,
      };
    } catch (error) {
      if (signal.aborted) {
        return;
      }

      this.state.jobPreview = {
        ...this.state.jobPreview,
        status: 'error',
        error:
          error instanceof Error
            ? error.message
            : 'Unable to load job preview.',
      };
    } finally {
      if (!signal.aborted) {
        this.jobPreviewRequest.complete();
      }
    }
  }

  private async loadStockMotionPreviewData() {
    const apiKey = this.getApiKeyValue();
    const { stockMotionPreview } = this.state;

    if (!apiKey || !stockMotionPreview.motionId) {
      this.state.stockMotionPreview = {
        ...INITIAL_STOCK_MOTION_PREVIEW_STATE,
        status: 'error',
        error: 'Unable to load preview.',
      };
      return;
    }

    const signal = this.stockMotionPreviewRequest.start();

    try {
      const bvhURL = await fetchStockMotionPreviewBvhURL(
        stockMotionPreview.motionId,
        apiKey,
        signal,
      );

      if (signal.aborted) {
        return;
      }

      this.state.stockMotionPreview = {
        ...this.state.stockMotionPreview,
        status: 'ready',
        bvhURL,
      };
    } catch (error) {
      if (signal.aborted) {
        return;
      }

      this.state.stockMotionPreview = {
        ...this.state.stockMotionPreview,
        status: 'error',
        error:
          error instanceof Error
            ? error.message
            : 'Unable to load motion preview.',
      };
    } finally {
      if (!signal.aborted) {
        this.stockMotionPreviewRequest.complete();
      }
    }
  }
}

export const { useStore, useStoreApi, Provider, Context } = createStoreProvider(
  {
    Store: AssetLibraryStore,
    displayName: 'AssetLibraryStore',
    staticDeps: { global: globalStores },
  },
);
